// @flow

import Formatic from 'formatic';
import _ from 'lodash';
import memoize from 'memoize-weak';
import { routeTo } from 'redux-router-kit';
import { $none, $set, $slice, update } from 'qim';
import { createSelector, createStructuredSelector } from 'reselect';

import { arrayCache, isEmptyString, objectCache } from '@zapier/common-utils';

import FlowActions from 'app/beta-editor/FlowActions';
import NodeLabels from 'app/beta-editor/NodeLabels';
import Pages from 'app/beta-editor/Pages';
import ServiceUtils from 'app/service/ServiceUtils';
import Step from 'app/beta-editor/Step';
import createDeepEqualsSelector from 'app/perf-utils/createDeepEqualsSelector';
import formaticFieldsPlugin from 'app/beta-editor/formaticFieldsPlugin';
import formaticPlugin from 'app/common/formaticPlugin';
import z from 'app/common/z';
import NodeUtils, {
  getNodeType,
  getServiceAssociatedWithNode,
  getStepTitle,
  getStepTitleWithServiceName,
  isActivatableNode,
  isEmptyActionNode,
  isValidFields,
} from 'app/beta-editor/utils/NodeUtils';
import { filterCannotContinue } from 'app/beta-editor/utils/FieldUtils';
import { getRollbackNode } from 'app/beta-editor/utils/TaskStateUtils';
import { getTriggerSamples } from 'app/beta-editor/Selectors';
import { nodeTypes } from 'app/service/constants';
import {
  DEFAULT_BETA_ACTION_LABEL,
  DEFAULT_BETA_TRIGGER_LABEL,
  EDITOR_ROUTE_PATH,
  INSTANT_ROUTE_PATH,
  PATH_SETTINGS_PAGE,
  ZT_API_NAMESPACE,
  ZT_ROUTE_PATH,
} from 'app/beta-editor/constants';
import {
  NORMAL_EDIT_MODE,
  UNKNOWN_EDIT_MODE,
} from 'app/beta-editor/constants/EditModes';
import {
  ZAP_META_ID,
  getSelectedSample,
} from 'app/beta-editor/utils/MultiSampleUtils';
import { ZAP_OFF, ZAP_ON, ZAP_SAVING } from 'app/beta-editor/flowState';
import {
  addParamsToUrl,
  addTruthyParamsToUrl,
  stripTrailingSlash,
} from 'app/common/utils/UrlUtils';
import {
  areAllPathsActivatable,
  getAllPaths,
  getNodeLevels,
  getNodeStepNumbers,
  getNodesForPath,
  getPathColor,
  getPathEmoji,
  getPathName,
  getRootNodeOfPath,
  isLastPathInGroup,
  isNodeInPath,
  isPathNode,
  isPathsGroupNode,
} from 'app/beta-editor/utils/PathUtils';
import {
  cleanObjectOfEmptyValues,
  exists,
  flattenObj,
  replaceParams,
} from 'app/common/CommonUtils';
import {
  getAncestors,
  getChildren,
  getParentId,
  getRootNode,
} from 'app/beta-editor/utils/NodeTrees';
import { isFilter, isRead, isSearch, isWrite } from 'app/service/utils';
import {
  nodeTemplates,
  selectNodeStepsWithIndexes,
} from 'app/shared-zap/SharedZapUtils';

import type { ZapOutlineType } from '@zapier/editor';

import type { AuthMap } from 'app/auth/types';
import type {
  EditMode,
  Flow,
  FlowNode,
  FlowNodeType,
  FlowStep,
  FlowSteps,
  FlowTaskState,
  MaybeSkinnyFlow,
  Need,
  NodeMap,
  NodeMeta,
  NodesSampleData,
  ZapState,
  ZapStatus,
} from 'app/beta-editor/flowState';
import type { ErrorType } from 'app/error/types';
import type {
  FlowError,
  FlowErrorWithMessage,
  FlowPage,
  FlowPages,
  StepType,
  SubStepType,
} from 'app/beta-editor/types';
import type { ID } from 'app/common/types';
import type { MaybeSampleOrSamples } from 'app/beta-editor/components/SamplePage';
import type { PageFactory } from 'app/beta-editor/components/PageComponents';
import type { Service, ServicesMap } from 'app/service/types';
import type { SharedZapType } from 'app/explore/utils/types';

const getStepFromNode = (node: FlowNode): FlowStep =>
  Step.create(getNodeType(node), {
    nodeId: node.id,
    selected_api: node.selected_api,
    action: node.action,
  });

// Turn nodes into ordered steps.
export const stepsFromNodes = memoize((nodes: FlowNode[]): FlowSteps => {
  const steps = nodes.map(getStepFromNode);
  return _.flatten(steps);
});

// !!! IMPORTANT !!!
// This function affects the global flows redux state. You
// may need to make the same changes to both the beta-editor
// and classic-editor code. Edit with caution and test thoroughly.
export const getSteps = memoize(
  (flowId: ID, nodes: NodeMap, nodeId: ID): FlowSteps => {
    const stepNodes = getNodesForPath(flowId, nodes, nodeId);
    return stepsFromNodes(stepNodes);
  }
);

// !!! IMPORTANT !!!
// This function affects the global flows redux state. You
// may need to make the same changes to both the beta-editor
// and classic-editor code. Edit with caution and test thoroughly.
const getNodeIdToStepIndex = memoize((steps: Step[]) =>
  _.fromPairs(
    _.compact(
      steps.map((step, i) => {
        if (!step.isNode) {
          return null;
        }
        return [step.nodeId, i];
      })
    )
  )
);

export const getStepIndex = (flowId: ID, nodes: NodeMap, nodeId?: ?ID) => {
  const rootNodeOfPath = getRootNodeOfPath(flowId, nodes, nodeId);
  const steps = getSteps(flowId, nodes, rootNodeOfPath.id);
  const nodeIdToStepIndex = getNodeIdToStepIndex(steps);

  const index = nodeIdToStepIndex[nodeId];
  return _.isNumber(index) ? index : null;
};

// !!! IMPORTANT !!!
// This function affects the global flows redux state. You
// may need to make the same changes to both the beta-editor
// and classic-editor code. Edit with caution and test thoroughly.
export const getNextNodeId = (flow: Flow, nodeId: ID) => {
  const steps = _.get(flow, ['steps'], []);
  const nodeIdToStepIndex = getNodeIdToStepIndex(steps);
  const stepIndex = nodeIdToStepIndex[nodeId];
  if (typeof stepIndex === 'number' && steps[stepIndex + 1]) {
    return steps[stepIndex + 1].nodeId;
  }
  return null;
};

export const getLastNodeInZap = (flow: Flow) => {
  const steps = _.get(flow, ['steps'], []);
  const lastStep = _.last(steps) || {};
  return _.get(flow, ['nodes', lastStep.nodeId], null);
};

// built in apps that should require a next step
const MIDDLE_STEP_APPS = [
  'FilterAPI',
  'ZapierTranslateDevAPI',
  'ZapierFormatterDevAPI',
  'WeatherDevAPI',
  'DelayAPI',
  'ZapierLeadInformationDevAPI',
];

export const buildMiddleStepMessage = (
  lastNodeService: Service,
  pathName: string
) => {
  const shouldUseGeneric = !lastNodeService || !lastNodeService.name;
  const middleStepAppOrGeneric = shouldUseGeneric
    ? 'The last step you’ve chosen'
    : lastNodeService.name;
  const maybeSteps = shouldUseGeneric ? '' : 'steps ';
  const maybeS = shouldUseGeneric ? 's' : '';
  const maybePath = pathName ? ` ${pathName} to` : '';

  return `${middleStepAppOrGeneric ||
    ''} ${maybeSteps}require${maybeS} a following step. Add one to${maybePath} finish your zap!`;
};

const maybeTriggerError = (flow: Flow) => {
  const hasTrigger = _.some(flow.nodes, node => isRead(node.type_of));
  if (!hasTrigger) {
    return { message: 'Your Zap currently lacks a Trigger step.' };
  }
  return null;
};

export const isMiddleStepApi = (api: string) =>
  _.includes(MIDDLE_STEP_APPS, api);

const isMiddleStepNode = node =>
  isMiddleStepApi(node.selected_api) || isPathNode(node);

export const getLastStepIsMiddleStepErrors = (
  flowId: ID,
  nodes: NodeMap,
  services: ServicesMap,
  addNodeCallback?: ID => void
) => {
  const allPaths = getAllPaths(flowId, nodes);
  const lastNodes = allPaths.map(_.last);
  const middleStepNodes = _.filter(lastNodes, isMiddleStepNode);
  const errors = middleStepNodes.map(node => {
    const service = NodeUtils.service(node, services) || {};
    const rootNode = getRootNodeOfPath(flowId, nodes, node.id);
    const pathName = isPathNode(rootNode) ? getPathName(rootNode) : '';
    return {
      kind: 'middle-step-is-last',
      nodeId: node.id,
      message: buildMiddleStepMessage(service, pathName),
      callback: addNodeCallback
        ? _.partial(addNodeCallback, node.id)
        : undefined,
    };
  });
  return _.sortBy(errors, 'message');
};

// !!! IMPORTANT !!!
// This function affects the global flows redux state. You
// may need to make the same changes to both the beta-editor
// and classic-editor code. Edit with caution and test thoroughly.
const getRootMeta = nodes => nodes && _.get(getRootNode(nodes), ['meta']);

// !!! IMPORTANT !!!
// This function affects the global flows redux state. You
// may need to make the same changes to both the beta-editor
// and classic-editor code. Edit with caution and test thoroughly.
export const createEditorUrl = ({
  flowId,
  nodeId,
  pageKey,
  isTemplate,
  query,
  isInstantZap,
}: {
  flowId?: ID,
  nodeId?: ID,
  pageKey?: string,
  isTemplate?: boolean,
  query?: Object,
  isInstantZap?: boolean,
} = {}) => {
  // Remove query params that have false as the value such as {simple: false}
  const truthyQuery = _.omitBy(query, isEmptyString);

  if (isInstantZap && flowId) {
    const editorUrl = `/${INSTANT_ROUTE_PATH}/${flowId}`;
    return addParamsToUrl(editorUrl, truthyQuery);
  }

  if (query && query.simple) {
    const editorUrl = `/${EDITOR_ROUTE_PATH}/${flowId ? flowId : ''}`;
    return addParamsToUrl(editorUrl, truthyQuery);
  }
  const zapTemplateSegment = isTemplate ? ZT_ROUTE_PATH : '';
  const flowSegment = flowId || '';
  const nodeSegment = nodeId ? `nodes/${nodeId}` : '';
  const pageKeySegment = pageKey || '';
  const segments = [
    zapTemplateSegment,
    flowSegment,
    nodeSegment,
    pageKeySegment,
  ]
    .filter(Boolean)
    .join('/');

  const editorUrl = stripTrailingSlash(`/${EDITOR_ROUTE_PATH}/${segments}`);
  return addParamsToUrl(editorUrl, truthyQuery);
};

export const createEditorFinishUrl = ({ flowId }: { flowId: ID }) =>
  createEditorUrl({
    flowId,
    pageKey: 'finish',
  });

// !!! IMPORTANT !!!
// This function affects the global flows redux state. You
// may need to make the same changes to both the beta-editor
// and classic-editor code. Edit with caution and test thoroughly.
export const configureUrlForTemplateMode = (
  url: string,
  isTemplate: boolean
): string =>
  addTruthyParamsToUrl(stripTrailingSlash(`${z.cfg.api}/${url}`), {
    [ZT_API_NAMESPACE]: isTemplate,
  });

type ResourceArgs = { id?: string, isTemplate: boolean };

// !!! IMPORTANT !!!
// This function affects the global flows redux state. You
// may need to make the same changes to both the beta-editor
// and classic-editor code. Edit with caution and test thoroughly.
export const getZapUrl = ({ id = '', isTemplate }: ResourceArgs = {}) =>
  configureUrlForTemplateMode(`zaps/${id}`, isTemplate);

// !!! IMPORTANT !!!
// This function affects the global flows redux state. You
// may need to make the same changes to both the beta-editor
// and classic-editor code. Edit with caution and test thoroughly.
export const getNodesUrl = ({ id = '', isTemplate }: ResourceArgs = {}) =>
  configureUrlForTemplateMode(`nodes/${id}`, isTemplate);

export const getZapNodesUrl = ({
  id,
  isTemplate,
}: { ...$Exact<ResourceArgs>, id: string } = {}) =>
  configureUrlForTemplateMode(`zaps/${id}/nodes`, isTemplate);

export const isFlowTemplate = (nodes: Array<FlowNode> | NodeMap) => {
  return _.get(getRootNode(nodes), ZT_API_NAMESPACE, false);
};

// Sort errors by path, then step number.
// Top level step errors come before errors on path steps.
export const sortFlowErrors = (errors: FlowError[]): FlowError[] =>
  errors.slice(0).sort((a, b) => {
    if (a.pathName && !b.pathName) {
      return 1;
    }
    if (!a.pathName && b.pathName) {
      return -1;
    }
    if (a.pathName && b.pathName && a.pathName !== b.pathName) {
      return a.pathName.localeCompare(b.pathName);
    }

    return a.stepNum - b.stepNum;
  });

export const createNodeMapFromArray = (nodes: Array<FlowNode>): NodeMap =>
  _.reduce(
    nodes,
    (acc, node) => {
      acc[node.id] = node;
      return acc;
    },
    {}
  );

export const getStepNum = (flow: Flow, nodeId: ?ID) => {
  const stepIndex = getStepIndex(flow.id, flow.nodes, nodeId);
  return stepIndex !== null ? stepIndex + 1 : undefined;
};

type IsValidNodeFn = (...*) => boolean;

type GetFlowErrorsOptions = {
  shouldIgnoreEmptyActions?: boolean,
};

const getNodeErrors = (
  flow: Flow,
  nodes: NodeMap | Array<FlowNode>,
  services: ServicesMap,
  auths?: {} = {},
  isValidNode: IsValidNodeFn,
  { shouldIgnoreEmptyActions }: GetFlowErrorsOptions = {}
): FlowError[] => {
  const invalidNodes = _.reject(
    nodes,
    node =>
      isValidNode(node, flow, services) ||
      (shouldIgnoreEmptyActions && isEmptyActionNode(node, services))
  );
  const isTemplate = isFlowTemplate(flow.nodes);
  const errors = _.map(invalidNodes, node => {
    const maybePathNode = getRootNodeOfPath(flow.id, flow.nodes, node.id);

    const pathName = isPathNode(maybePathNode)
      ? getPathName(maybePathNode)
      : '';

    // We default path names to 'Path A', 'Path B', etc. To avoid saying
    // things like 'Path Path A, Step 1', remove any leading 'Path ' from the path name;
    const shortPathName = pathName.replace(/^Path\s/, '');

    const pathClause = pathName ? `Path ${shortPathName}, ` : '';
    const stepNum = getStepIndex(flow.id, flow.nodes, node.id) + 1;
    const groupId = `${maybePathNode.id}-${stepNum}`;

    const appPageKey = isPathNode(node) ? 'path' : 'setup';
    const appPageHref = createEditorUrl({
      flowId: flow.id,
      nodeId: node.id,
      pageKey: appPageKey,
      isTemplate,
    });
    const subStep = Pages.nextPageInNode({
      flow,
      node,
      nodeId: node.id,
      services,
      auths,
      shouldUseSimpleZapSetup: false,
    });

    return {
      href: subStep ? subStep.href : appPageHref,
      stepNum,
      pathName,
      stepLabel: `${pathClause}Step ${stepNum}`,
      pageKey: _.get(subStep, 'key') || appPageKey,
      nodeId: node.id,
      groupId,
    };
  });

  return sortFlowErrors(errors);
};

export const maybeInvalidNodeErrors = (
  flow: Flow,
  services: ServicesMap,
  auths: {},
  options: GetFlowErrorsOptions = {}
): FlowErrorWithMessage[] => {
  const errors = getNodeErrors(
    flow,
    flow.nodes,
    services,
    auths,
    NodeUtils.isValid,
    options
  );
  return errors.map(error => ({
    ...error,
    message: `${error.stepLabel} has an error that requires attention.`,
  }));
};

export const getUnfinishedStepErrors = (
  flow: Flow,
  services: ServicesMap,
  auths?: {} = {},
  currentNodeId: ID
): FlowError[] => {
  const errors = getNodeErrors(
    flow,
    getAncestors(flow.nodes, currentNodeId),
    services,
    auths,
    isActivatableNode
  );

  const currentNode = _.get(flow, ['nodes', currentNodeId]);
  const errorsForThisNode = getNodeErrors(
    flow,
    [currentNode],
    services,
    auths,
    isValidFields
  );
  return errors.concat(errorsForThisNode);
};

const hasAction = flow =>
  _.some(flow.nodes, node => {
    const type = node.type_of;
    return type === nodeTypes.WRITE || type === nodeTypes.SEARCH_OR_WRITE;
  });

const maybeActionError = (flow: Flow, addNodeCallback?: ID => void) =>
  !_.isEmpty(flow.nodes) && !hasAction(flow)
    ? {
        kind: 'action-is-missing',
        nodeId: getLastNodeInZap(flow).id,
        message: 'Your Zap currently lacks an Action step. Add one now!',
        callback: addNodeCallback
          ? _.partial(addNodeCallback, getLastNodeInZap(flow).id)
          : undefined,
      }
    : undefined;

/**
 * Returns lists of errors in the flow (the zap).
 *
 * If the optional addNodeCallback is called, it will be used as the error
 * callback for when the zap does not have an action.
 *
 * Possible improvements:
 *    * Better error ordering
 *    * Specific error messages
 *    * 1 error message per sub-step instead of step
 */
export const getFlowErrors = memoize(
  (
    flow: Flow,
    services: ServicesMap,
    auths: AuthMap,
    addNodeCallback?: (parentId: ID) => void,
    options: GetFlowErrorsOptions = {}
  ) => {
    const nodeErrors = maybeInvalidNodeErrors(flow, services, auths, options);
    const lastStepIsMiddleStepErrors = getLastStepIsMiddleStepErrors(
      flow.id,
      flow.nodes,
      services,
      addNodeCallback
    );
    // If we tell user to add final step don't also tell them we need an action
    const actionError =
      lastStepIsMiddleStepErrors.length > 0
        ? undefined
        : maybeActionError(flow, addNodeCallback);
    return _.compact([
      // put general errors first:
      actionError,
      maybeTriggerError(flow),
      ...lastStepIsMiddleStepErrors,

      // then node specific errors
      ...nodeErrors,
    ]);
  }
);

export const hasFlowErrors = (flow: Flow, services: ServicesMap, auths: {}) => {
  return !_.isEmpty(getFlowErrors(flow, services, auths));
};

export const isPaused = (flow: Flow) => {
  return flow.paused;
};

export const isUnpaused = (flow: Flow) => !isPaused(flow);

export const canUnpause = (flow: Flow, services: ServicesMap) => {
  if (isFlowTemplate(flow.nodes)) {
    return false;
  }
  const nodesPreventingActivation = _.reject(flow.nodes, node => {
    return (
      // paths group nodes never have any setup
      isPathsGroupNode(node) ||
      NodeUtils.isActivatableNode(node, flow, services)
    );
  });
  const hasTriggerAndActionAndNoMiddleStepErrors = _.isEmpty(
    _.compact([
      ...getLastStepIsMiddleStepErrors(flow.id, flow.nodes, services),
      maybeActionError(flow),
      maybeTriggerError(flow),
    ])
  );
  const isWaiting =
    _.get(flow, ['sampleStates', 'isWaitingForSample']) &&
    _.get(flow, ['sampleStates', 'isPollingForSample']);

  return (
    isPaused(flow) &&
    _.isEmpty(nodesPreventingActivation) &&
    hasTriggerAndActionAndNoMiddleStepErrors &&
    !isWaiting
  );
};

/*
 Returns true if step or node is in an error state.
*/

export const isError = (
  node: FlowNode,
  { taskState, dirtyTaskState, nodeLoading, staleSamples }: NodesSampleData
) => {
  const sampleStatus = NodeUtils.sampleStatus(node, {
    taskState,
    dirtyTaskState,
    nodeLoading,
    staleSamples,
  });

  // don't return true on 'out-of-sync' -
  // will cause error icon to appear as the user is typing options!
  return sampleStatus === 'error';
};

/*
   Performs a basic sanity check on the flow object, checking for things like
   missing service defintions, etc. These are really bad errors that *should*
   never happen, but do if a zap is created from a shared template that is
   messed up, etc.
 */
export const sanityCheck = (flow: Flow, services: ServicesMap) => {
  const results = [];

  _.each(flow.nodes, node => {
    if (exists(node.selected_api) && !exists(services[node.selected_api])) {
      // just return if missing service def - don't dig the hole any deeper
      return results.push({
        summary: `Missing service definition for ${node.selected_api}`,
      });
    }

    if (exists(node.action) && !exists(NodeUtils.nodeAction(node, services))) {
      // Users can still function when this happens. We may consider sending to sentry later.
      console.warn(`Missing action definition for node ${node.id}`);
    }

    return undefined;
  });

  return _.uniqBy(results, e => e.summary);
};

const nodeStateContainers = [
  'dirtyNeeds',
  'dirtyTaskState',
  'needs',
  'needsMeta',
  'nodeLoading',
  'nodes',
  'nodeUpdateStamps',
  'pages',
  'staleSamples',
];

const removeNodeIdFromFlowContainer = _.curry(
  (nodeId, flow: Flow, containerKey: string) => {
    flow[containerKey] = _.omit(flow[containerKey], nodeId);
    return flow;
  }
);

const removeNodeIdFromFlowState = (nodeId, flow: Flow) => {
  const newFlow = nodeStateContainers.reduce(
    (removeNodeIdFromFlowContainer(nodeId): $FlowFixMe),
    flow
  );
  newFlow.taskState = getRollbackNode(newFlow.taskState, nodeId);
  return newFlow;
};

// The _.curry call in removeNodeIdFromFlowContainer is this
// hard to flow type. Just return any for now.
export const removeNodeFromFlow = (
  flow: MaybeSkinnyFlow,
  nodeId: ID
): $FlowFixMe => {
  const newFlow = { ...flow };
  //  Remove from `nodeIds`
  const nodeIndex = newFlow.nodeIds.indexOf(nodeId);
  newFlow.nodeIds = update(
    [$slice(nodeIndex, nodeIndex + 1), $set([])],
    newFlow.nodeIds
  );

  const nodesRemovedFlow = update(['nodes', nodeId, $none], newFlow);

  // Remove all traces of node state within `nodesRemovedFlow`
  return removeNodeIdFromFlowState(`${nodeId}`, nodesRemovedFlow);
};

// !!! IMPORTANT !!!
// This function affects the global flows redux state. You
// may need to make the same changes to both the beta-editor
// and classic-editor code. Edit with caution and test thoroughly.
export const getEditMode = (flow: ?Flow) => {
  const rootMeta = flow ? getRootMeta(flow.nodes) : null;

  if (!rootMeta) {
    return _.get(flow, 'edit_mode', UNKNOWN_EDIT_MODE);
  }

  const rootEditMode = _.get(flow, ['temporaryEditMode'], rootMeta.edit_mode);

  return rootEditMode ? rootEditMode : NORMAL_EDIT_MODE;
};

// !!! IMPORTANT !!!
// This function affects the global flows redux state. You
// may need to make the same changes to both the beta-editor
// and classic-editor code. Edit with caution and test thoroughly.
export const setEditMode = (flow: Flow, newMode: EditMode) => {
  if (newMode !== NORMAL_EDIT_MODE) {
    throw new Error('Not a valid mode!');
  }

  return update(['nodes', flow.id, 'meta', 'edit_mode', $set(newMode)], flow);
};

export const getReferrer = (flow: Flow) => {
  const rootMeta = getRootMeta(flow.nodes);

  return rootMeta && rootMeta.referrer;
};

export const getSharedZapId = (flow: Flow) => {
  if (!flow) {
    return undefined;
  }

  const rootNode = getRootNode(flow.nodes);
  return _.get(rootNode, ['guided_recipe_origin_id']);
};

export const getCustomTemplate = (flow: Flow) => {
  if (!flow) {
    return undefined;
  }

  const rootNode = getRootNode(flow.nodes);
  return _.get(rootNode, ['meta', 'custom_template']);
};

// Check if a node is a match for a shared zap step.
const nodeMatchesStep = (node: FlowNode, step: FlowStep) => {
  return (
    node.type_of === step.type_of &&
    node.selected_api === step.selected_api &&
    node.action === step.action
  );
};

// A Shared Zap could have diverged since a Zap was created. Validate that the
// Shared Zap is still a good enough match.
export const matchesSharedZap = (flow: Flow, sharedZap: Flow) => {
  const sharedZapNodeTemplates = nodeTemplates(sharedZap);
  const rootNode = getRootNode(flow.nodes);
  return (
    sharedZapNodeTemplates.length > 0 &&
    rootNode &&
    nodeMatchesStep(rootNode, sharedZapNodeTemplates[0])
  );
};

// Create a mapping from node id to step index.
export const mapNodeIdsToSharedZapStepIndexes = (
  flow: Flow,
  sharedZap: Flow
) => {
  if (!flow) {
    return {};
  }
  const nodeSteps = selectNodeStepsWithIndexes(sharedZap);
  const rootNode = getRootNode(flow.nodes);
  if (!rootNode || nodeSteps.length === 0) {
    return {};
  }
  // Must match the root node, otherwise, forget about it.
  if (!nodeMatchesStep(rootNode, nodeSteps[0].step)) {
    return {};
  }
  const childNodes = flow.nodeIds.slice(1).map(nodeId => flow.nodes[nodeId]);
  const childNodeSteps = nodeSteps.slice(1);
  const matchedNodeIds = _.transform(
    childNodes,
    (result, node) => {
      if (childNodeSteps.length < result.length + 1) {
        return false;
      }
      if (nodeMatchesStep(node, childNodeSteps[result.length].step)) {
        result.push(node.id);
      }
      return undefined;
    },
    []
  );
  return [rootNode.id]
    .concat(matchedNodeIds)
    .reduce((result, nodeId, index) => {
      result[nodeId] = nodeSteps[index].index;
      return result;
    }, {});
};

// Normalize a flow object.
export const createFlow = (flow: Flow) => {
  const missingNeeds = flow.missingNeeds || {};
  const needs = flow.needs || {};
  const nodes = flow.nodes || {};
  const taskState = flow.taskState || {};
  const extraNeeds = flow.extraNeeds || {};

  flow = _.extend(
    {
      isLoaded: true,
      pages: {},
      errors: [],
      sampleStates: {}, // a random assortment of booleans
      staleSamples: {}, // which nodes no longer have fresh/valid samples
      dirtyTaskState: {}, // which nodes needs task state refreshed from server
      dirtyNeeds: {},
      needsMeta: {},
      nodeLoading: {},
      nodeUpdateStamps: {},
      givesAll: {}, // The gives a node will have provided to it by its ancestors
      openStepMenus: {}, // booleans of sidebar step menu states (truthy is open, falsy closed), keyed by zero based step index
      lastTestStatuses: {}, // most recently ran test statuses, keyed by node id, preserved because we wipe them out in the taskState
      removalStatus: 'initial',
      openAddStepPickerNodeId: null,
      isAddingOrRemovingStep: false,
    },
    flow,
    {
      nodes,
      needs,
      missingNeeds,
      taskState,
      extraNeeds,
    }
  );

  return flow;
};

export const isClientFlow = (flow: Flow) => !flow.id || flow.cid === flow.id;

export const getTriggerNode = (nodes: NodeMap) =>
  _.find(_.values(nodes), ({ type_of }: FlowNode) => type_of === 'read');

export const createNodeTemplates = (flow: Flow) => {
  const nodeIdsToIndex = flow.nodeIds.reduce((result, nodeId, i) => {
    result[nodeId] = i;
    return result;
  }, {});
  return flow.nodeIds.map((nodeId, i) => {
    const node = flow.nodes[nodeId];
    const params =
      i === 0
        ? node.params
        : replaceParams(node.params, (id, key) => {
            if (!id) {
              // Let's just assume that the param was referencing the trigger.
              return `0__${key}`;
            }
            if (flow.nodes[id]) {
              return `{{${nodeIdsToIndex[id]}__${key}}}`;
            }
            // Bad node id.
            return `[[INSERT FIELD FOR ${key}]]`;
          });
    return cleanObjectOfEmptyValues({
      type_of: node.type_of,
      selected_api: node.selected_api,
      action: node.action,
      title: i === 0 ? flow.nodes[nodeId].title : undefined,
      params,
      meta: cleanObjectOfEmptyValues({
        paramamp: _.get(node, ['meta', 'parammap']),
      }),
    });
  });
};

export const createAdHocTemplateUrl = (flow: Flow) => {
  const sharedZapNodeTemplates = createNodeTemplates(flow);
  const query = flattenObj({
    template: sharedZapNodeTemplates,
  });
  return addParamsToUrl('/app/editor', query);
};

// Provided a flow, will return the unique selected APIs associated with it
export const getFlowServices = (flow: Flow) => {
  const nodes = _.get(flow, ['nodes'], []);

  return _.flowRight(
    _.uniq,
    keys => _.filter(keys, Boolean),
    n => _.map(n, node => node.selected_api)
  )(nodes);
};

export const hasFilterSteps = (flow: Flow) => {
  const flowSteps = _.get(flow, 'steps');
  return !flow ? false : _.some(flowSteps, step => isFilter(step.type));
};

/*
   Return true if this is the last step of the zap or current path,
   and its not the trigger step or path settings step.
 */
export const isZapOrPathFinished = ({
  flow,
  nodeId,
}: {
  flow: Flow,
  nodeId: ID,
}) => {
  const stepIndex = getStepIndex(flow.id, flow.nodes, nodeId);
  const isLastStep = flow.steps.length === stepIndex + 1;
  return isLastStep && stepIndex !== 0;
};

export const isTriggerStep = (node: FlowNode) =>
  isRead(_.get(node, ['type_of']));

export const isSearchStep = (node: FlowNode) =>
  isSearch(_.get(node, ['type_of']));

export const isFilterStep = (node: FlowNode) =>
  isFilter(_.get(node, ['type_of']));

export const isWriteStep = (node: FlowNode) =>
  isWrite(_.get(node, ['type_of']));

export const getTriggerSampleIds = (taskState: FlowTaskState, flowId: ID) =>
  _.map(getTriggerSamples(taskState, flowId), ZAP_META_ID);

export const isRemoving = ({ removalStatus }: Flow) =>
  removalStatus === 'removing';

export const hasFinishedRemoving = ({ removalStatus }: Flow) =>
  removalStatus === 'finished';

/*
     Get the state of a zap, either 'on', 'off', or 'draft' (ie
     incomplete).

     Papers over the fact that we have two props that can get out of
     sync: flow.state and flow.paused. The flow.paused prop takes
     precendence: If a zap is not paused, its state can only be 'on'. If
     a zap is paused, its state can only be 'off' or 'draft'.
   */
export const getZapState = (flow: Flow): ZapState => {
  // if the zap is not paused, it must be 'on':
  if (!flow.paused) {
    return 'on';
  }

  // if the zap is paused, it can not be 'on':
  if (flow.paused && flow.state === 'on') {
    return 'off';
  }

  // A paused zap could be in draft mode or off.
  return flow.state;
};

export const getNextUrlAfterRemoveNode = (
  flow: Flow,
  nodeId: ID,
  isZapTemplate?: boolean
) => {
  const node = flow.nodes[nodeId];
  const parentId = getParentId(node);
  if (node && parentId && isPathNode(node)) {
    const pathsGroupNode = flow.nodes[parentId];

    // If this is the last path in the paths group, the paths group
    // will also get removed, so route to the parent of the paths
    // group.
    const isLastPath = isLastPathInGroup(flow, node.id);
    const routeToNodeId = isLastPath
      ? getParentId(pathsGroupNode) || ''
      : pathsGroupNode.id;

    return createEditorUrl({
      flowId: flow.id,
      pageKey: isLastPath ? 'setup' : PATH_SETTINGS_PAGE,
      nodeId: routeToNodeId,
    });
  }

  const rootNode = getRootNodeOfPath(flow.id, flow.nodes, nodeId);
  if (isPathNode(rootNode)) {
    return createEditorUrl({
      flowId: flow.id,
      pageKey: 'path',
      nodeId: rootNode.id,
    });
  }

  return createEditorUrl({
    flowId: flow.id,
    pageKey: 'overview',
    isTemplate: isZapTemplate,
  });
};

type NodeFromPropsType = {
  flow: Flow,
  nodeId: ID,
};

export const nodeFromProps = (props: NodeFromPropsType): ?FlowNode =>
  _.get(props, ['flow', 'nodes', props.nodeId]);

const isZapTemplateFromProps = ({ isZapTemplate }) => isZapTemplate;

const servicesFromProps = ({ services }) => services;

export const needsFromProps = (props: { nodeId: ID }) =>
  _.get(props, ['flow', 'needs', props.nodeId]);

const shouldDisableFieldsFromProps = createSelector(
  isZapTemplateFromProps,
  nodeFromProps,
  servicesFromProps,
  (isZapTemplate, node, services) =>
    isZapTemplate &&
    node &&
    !node.authentication_id &&
    NodeUtils.serviceNeedsAuth(node, services)
);

export const fieldsFromProps = createSelector(
  needsFromProps,
  shouldDisableFieldsFromProps,
  props => props.shouldUseSimpleZapSetup,
  (fields, shouldDisableField, shouldUseSimpleZapSetup) => {
    if (!_.isEmpty(fields) && shouldDisableField) {
      return fields.map((field: Need): Need => ({
        ...field,
        prefill: '',
        searchfill: false,
        alters_custom_fields: false,
      }));
    }

    if (shouldUseSimpleZapSetup && Array.isArray(fields)) {
      const requiredDropdowns = fields.filter(
        field =>
          field.required &&
          (field.prefill || (field.choices && !_.isEmpty(field.choices)))
      );

      return requiredDropdowns;
    }

    return fields;
  }
);

export const getSimplifiedStepHeader = (
  nodes: NodeMap,
  step: FlowStep,
  services: ServicesMap
) => {
  const node = nodes[step.nodeId];
  const stepTitle = node && node.meta && node.meta.stepTitle;
  if (stepTitle) {
    return stepTitle;
  } else {
    const service = NodeUtils.service(node, services);
    const actionLabel = NodeLabels.actionLabel(node, services);
    return service && actionLabel
      ? `${actionLabel}${
          ServiceUtils.isBuiltIn(service)
            ? ''
            : ` in ${ServiceUtils.getServiceName(service)}`
        }`
      : null;
  }
};

type NodeDescriptor = { nodeId: ?ID, type: FlowNodeType };

export const getStepLabelFromNode = (
  node?: FlowNode,
  step: FlowStep | NodeDescriptor,
  services: ServicesMap
) => {
  const service = node && NodeUtils.service(node, services);
  const actionLabel = node && NodeLabels.actionLabel(node, services);
  if (service && actionLabel) {
    return actionLabel;
  }

  if (step.type === 'read') {
    return DEFAULT_BETA_TRIGGER_LABEL;
  }

  return DEFAULT_BETA_ACTION_LABEL;
};

export const getStepLabel = (
  nodes: NodeMap,
  step: FlowStep | NodeDescriptor,
  services: ServicesMap
) => {
  if (step.nodeId === undefined) {
    return '';
  }
  const node = step.nodeId ? nodes[step.nodeId] : undefined;
  return getStepLabelFromNode(node, step, services);
};

/*
Get the class name for a sub-step link, based on the page statuss
*/
const getSubStepStatus = (page: FlowPage) => {
  if (page.isLocked) {
    return 'locked';
  } else if (page.status === 'success' || page.skipDuringWalkthrough) {
    return 'completed';
  } else if (!page.status) {
    // blank means incomplete
    return 'incomplete';
  } else {
    return page.status; // alert, waiting
  }
};

const subStepFactory = objectCache();

const getSubSteps = (
  step: FlowStep,
  nodeId: ID,
  pages: FlowPages
): SubStepType[] => {
  return pages[step.nodeId]
    .filter(page => page.visible)
    .map(page =>
      subStepFactory({
        key: page.key,
        label: page.menuLabel || page.label,
        data: page.data,
        href: page.href,
        isLoading: page.isLoading,
        isLocked: page.isLocked,
        status: getSubStepStatus(page),
        nodeId,
      })
    );
};

const completedSubStepCount = (subSteps: SubStepType[], count: number = 0) => {
  if (subSteps.length < count + 1) {
    return count;
  }
  if (subSteps[count].status === 'completed') {
    return completedSubStepCount(subSteps, count + 1);
  }
  return count;
};

const getAppId = (node, services) => {
  const service = _.find(services, svc => svc.id === node.selected_api);
  return _.get(service, 'id');
};

export const getZapOutline = (
  nodes: NodeMap,
  services: ServicesMap
): ZapOutlineType =>
  getNodeLevels(nodes).map((item, i) => {
    const node = _.get(nodes, item.nodeId, {});
    const pathNode = _.get(nodes, item.pathId);

    const stepTitle = isPathsGroupNode(node)
      ? 'Run these Paths'
      : getStepTitleWithServiceName({
          node,
          services,
        });

    const pathInfo = pathNode
      ? {
          pathColor: getPathColor(pathNode),
          pathEmoji: getPathEmoji(pathNode),
        }
      : {};

    const service = getServiceAssociatedWithNode(node, services);
    const serviceName = service ? ServiceUtils.getServiceName(service) : '';

    return {
      ...item,
      ...pathInfo,
      appId: getAppId(node, services),
      extraSearchTerms: serviceName,
      name: `${i + 1}. ${stepTitle}`,
      index: i,
      isCollapsible: isPathsGroupNode(node),
      shouldHideAppLogo: isPathNode(node),
    };
  });

const selectFlowId = (flow: Flow) => flow.id;
const selectNodes = (flow: Flow) => flow.nodes;
const selectSteps = (flow: Flow) => flow.steps;

const selectTaskState = (flow: Flow) => flow.taskState;
const selectDirtyTaskState = (flow: Flow) => flow.dirtyTaskState;
const selectNodeLoading = (flow: Flow) => flow.nodeLoading;
const selectStaleSamples = (flow: Flow) => flow.staleSamples;

export const selectNeeds = (flow: Flow, nodeId: ID) =>
  _.get(flow, ['needs', nodeId], []);

export const areNeedsEmpty = (flow: Flow, nodeId: ID) =>
  _.isEmpty(selectNeeds(flow, nodeId));

export const areNeedsDirty = (flow: Flow, nodeId: ID) =>
  Boolean(flow.dirtyNeeds[nodeId]);

const selectNodesSampleData = createStructuredSelector({
  taskState: selectTaskState,
  dirtyTaskState: selectDirtyTaskState,
  nodeLoading: selectNodeLoading,
  staleSamples: selectStaleSamples,
});

type OnAddStep = (stepType: string, options?: {}) => void;

type SelectFlowStepsProps = {
  pages: FlowPages,
  services: ServicesMap,
  onAddStep: OnAddStep,
};

const selectPages = (flow: Flow, props: SelectFlowStepsProps) => props.pages;

const selectServices = (flow: Flow, props: SelectFlowStepsProps) =>
  props.services;
const selectOnAddStep = (flow: Flow, props: SelectFlowStepsProps) =>
  props.onAddStep;

const stepCache = objectCache();
const flowStepsCache = arrayCache();

const EMPTY_PATH_NODES = [];

export const selectFlowSteps = createDeepEqualsSelector(
  selectFlowId,
  selectNodes,
  selectSteps,
  selectNodesSampleData,
  getEditMode,
  selectPages,
  selectServices,
  selectOnAddStep,
  (
    flowId: ID,
    nodes: NodeMap,
    steps,
    nodesSampleData,
    editMode,
    pages: FlowPages,
    services: ServicesMap,
    onAddStep: OnAddStep
  ): StepType[] => {
    const stepNumberByNodeId = getNodeStepNumbers(nodes);

    const hasNodeError = node => isError(node, nodesSampleData);

    const flowSteps = (steps || []).reduce((newSteps, step: FlowStep, i) => {
      const node = nodes[step.nodeId];
      const api = node.selected_api;
      const type = step.type;
      const isNode = step.isNode;
      const label = getStepLabel(nodes, step, services);
      const title = getStepTitle(node);
      const stepTypeLabel = NodeLabels.actionType(node);
      const subSteps = getSubSteps(step, step.nodeId, pages);

      const completedCount = completedSubStepCount(subSteps);
      const isComplete = isPathsGroupNode(node)
        ? areAllPathsActivatable(
            flowId,
            nodes,
            nodesSampleData,
            node.id,
            services
          )
        : completedCount === subSteps.length;
      const hasError = hasNodeError(node);

      const pathNodes = isPathsGroupNode(node)
        ? getChildren(nodes, step.nodeId)
        : EMPTY_PATH_NODES;
      const simplifiedHeader = getSimplifiedStepHeader(nodes, step, services);

      const stepNumberForNode = _.get(stepNumberByNodeId, [step.nodeId], 1);

      const newStep = stepCache({
        api,
        completedCount,
        flowId,
        isComplete,
        isNode,
        isError: hasError,
        label,
        node, //suspect
        nodeId: step.nodeId,
        onAddStep,
        pathNodes,
        simplifiedHeader,
        stepIndex: i,
        stepNumber: stepNumberForNode,
        stepTypeLabel,
        subSteps,
        title,
        type,
      });
      newSteps.push(newStep);
      return newSteps;
    }, []);

    return flowStepsCache(flowSteps);
  }
);

export const getNode = (step: FlowStep | StepType | {}): FlowNode =>
  _.get(step, 'node');

type HasNeedsProps = {
  flow: Flow,
  node: FlowNode,
};

export const hasNeeds = ({ flow, node }: HasNeedsProps) => {
  const needs = (flow.needs || {})[node.id];
  return needs && needs.length > 0;
};

export const isSetupBehaviorComplete = (node: FlowNode) => {
  const hasApp = Boolean(node && node.selected_api);
  const hasValidAction = Boolean(node && node.action);

  return hasApp && hasValidAction;
};

export const canSimpleZapSetupBeTurnedOn = (props: {
  flow: Flow,
  services: ServicesMap,
  latestError?: ErrorType,
  auths: {},
  isZapTemplate: boolean,
  shouldUseSimpleZapSetup: boolean,
  nodeId: ID,
}) => {
  const { flow, services, auths, latestError } = props;
  const nodes = _.get(flow, 'nodes');

  if (latestError) {
    return false;
  }

  const formaticConfig = Formatic.createConfig(
    formaticPlugin,
    Formatic.plugins.meta,
    config =>
      formaticFieldsPlugin(config, {
        shouldUseDefaults: false,
        isZapTemplate: false,
        shouldUseSimpleZapSetup: true,
      })
  );

  return Object.keys(nodes).every((key: ID) => {
    const node = nodes[key];
    const { id: nodeId } = node;
    const fields = fieldsFromProps({ ...props, nodeId });
    const service = NodeUtils.service(node, services);

    const authId = node.authentication_id;
    const isAuthStale = auths[authId] && auths[authId].is_stale;
    const isValidAuth = NodeUtils.isValidAuth(node, services) && !isAuthStale;

    const isFormValid = formaticConfig.isValidRootValue({
      fields,
      value: node.params,
      meta: {
        flow,
        node,
        service,
      },
    });

    return isValidAuth && isFormValid;
  });
};

export const determineZapStatus = (flow: Flow): ZapStatus => {
  if (flow.isLoaded) {
    if (flow.isToggling) {
      return ZAP_SAVING;
    }
    if (isPaused(flow)) {
      return ZAP_OFF;
    }
    if (!isPaused(flow) && !flow.isToggling) {
      return ZAP_ON;
    }
  }
  return ZAP_OFF;
};

const markMetaDirty = meta =>
  update(['validation', 'action', 'is_opened', $set(true)], meta);

export const markWebhookMetaDirty = (meta: NodeMeta) =>
  update(['validation', 'webhook', 'is_opened', $set(true)], meta);

export const getActionAttrs = (
  node: FlowNode,
  newValue: { itemKey: string, itemType: string }
) => {
  const isUnchanged =
    newValue.itemKey === node.action && newValue.itemType === node.type_of;

  // Don't wipe out search_or_write if user selected on options page
  const isSameSearch =
    newValue.itemType === 'search' &&
    node.type_of === 'search_or_write' &&
    newValue.itemKey === node.action;

  const type_of = isSameSearch ? node.type_of : newValue.itemType;
  const desiredAttrs = {
    type_of,
    action: newValue.itemKey,
  };

  if (isUnchanged) {
    return {
      ...desiredAttrs,
      params: node.params,
      meta: markMetaDirty(node.meta),
    };
  } else {
    const overrideAttrs = {
      params: {},
      meta: markMetaDirty({}),
    };
    return NodeUtils.mergeNodeAttrs(node, desiredAttrs, overrideAttrs);
  }
};

export const isStepOpen = ({
  nodeId,
  routerNodeId,
}: {
  nodeId: ID,
  routerNodeId?: ID,
}) => nodeId === routerNodeId;

export const shouldHideTestingStepLink = (subStep: SubStepType) => {
  return (
    subStep.key === 'sample' &&
    (_.includes(['locked', 'incomplete'], subStep.status) || !subStep.status)
  );
};

const areAnyPreviousSubStepsIncomplete = (
  subSteps: Array<SubStepType>,
  currentSubStep: SubStepType
) => {
  const previousSubSteps = _.takeWhile(
    subSteps,
    subStep => subStep !== currentSubStep
  );
  const areSomePreviousIncomplete =
    !_.isEmpty(previousSubSteps) &&
    _.some(previousSubSteps, subStep =>
      _.includes(['locked', 'incomplete'], subStep.status)
    );
  return areSomePreviousIncomplete;
};

export const shouldHideActionStepLink = (
  step: StepType,
  subStep: SubStepType
) => areAnyPreviousSubStepsIncomplete(step.subSteps, subStep);

export const getSelectedSampleForNode = (
  flow: Flow,
  node: FlowNode
): MaybeSampleOrSamples => {
  const taskState = _.get(flow, ['taskState'], {});
  const stepSamples = _.get(taskState, ['output', node.id], []);
  // if we're on a trigger step, then get the selected sample
  if (flow.id === node.id) {
    return getSelectedSample({
      taskState,
      samples: stepSamples,
    });
    // otherwise, if for some reason we have an array of samples
    // (on search steps for example)
    // then just return the most recent one
  } else if (_.isArray(stepSamples)) {
    return _.head(stepSamples);
    // otherwise, we only have one sample, so just return it
  } else {
    return stepSamples;
  }
};

// !!! IMPORTANT !!!
// This function affects the global flows redux state. You
// may need to make the same changes to both the beta-editor
// and classic-editor code. Edit with caution and test thoroughly.
export const getContinueFromStepActions = ({
  flow,
  nodeId,
}: {
  flow: Flow,
  nodeId: ID,
}) => {
  const nextNodeId = getNextNodeId(flow, nodeId);

  if (nextNodeId) {
    return [
      routeTo(
        createEditorUrl({
          flowId: flow.id,
          nodeId: nextNodeId,
          pageKey: 'setup',
        })
      ),
    ];
  }

  // It's a little confusing but isZapOrPathFinished only tests if the zap
  // has more than one step and that this node is the last step.
  //
  // We also need to make sure that the last node is a "write" step and
  // not considered a "middle" step (e.g. Delay/Filter/anything defined in
  // MIDDLE_STEP_APPS above), which would indicate that the path is valid.
  const urlProps = { flowId: flow.id, nodeId: undefined };
  const node = flow.nodes[nodeId];
  const shouldShowAddStepPicker = !(
    isZapOrPathFinished({ flow, nodeId }) &&
    isWriteStep(flow.nodes[nodeId]) &&
    !isMiddleStepNode(node)
  );

  // Open add step picker, and route to the path node
  if (isNodeInPath(flow.id, flow.nodes, nodeId)) {
    const pathRootNode = getRootNodeOfPath(flow.id, flow.nodes, nodeId);
    urlProps.nodeId = pathRootNode.id;
  }

  return [
    shouldShowAddStepPicker && FlowActions.onOpenAddStepPicker(flow.id, nodeId),
    routeTo(createEditorUrl(urlProps)),
  ].filter(Boolean);
};

// There are other values, but I didn't need them for flow typing
type SharedZapStatus = 'published' | 'for-review';

export const hasStatus = (status: SharedZapStatus, sharedZap?: SharedZapType) =>
  _.get(sharedZap, 'status') === status;

export const isSharedZapPublished = _.partial(hasStatus, 'published');

export const needsUnpublishedWarning = (sharedZap: ?SharedZapType) =>
  !_.isEmpty(sharedZap) ? !isSharedZapPublished(sharedZap) : false;

// Check if zap is using a premium app by comparing nodes with services
export const hasPremiumAppInZap = (zap: Flow, services: Service[]): boolean => {
  const result = _.values(zap.nodes).some(node => {
    const service = services.find(s => s.id === node.selected_api);
    return !!service && service.isPremium;
  });
  return result;
};

export const hasValidPage = (
  pageKey: string,
  pageComponent: PageFactory<any>
) => {
  return exists(pageKey) && exists(pageComponent);
};

export const isNodeRemoved = (flow: Flow, nodeId: ID) => {
  return nodeId && !_.get(flow, ['nodes', nodeId]);
};

export const getSelectedFolderId = (flow: Flow) => {
  const folderIds = _.get(flow, 'folder_ids');
  const selectedFolderId = _.first(folderIds);
  return selectedFolderId;
};

type ShouldShowSimplifiedEditorFinishParams = {
  canUnpause: boolean,
  errors: Array<Object>,
  hasFlowErrors: boolean,
  isActivationPromptDismissed: boolean,
  isAnyNodeLoading: boolean,
};

export const shouldShowSimplifiedEditorFinish = ({
  canUnpause: _canUnpause,
  errors,
  hasFlowErrors: _hasFlowErrors,
  isActivationPromptDismissed,
  isAnyNodeLoading,
}: ShouldShowSimplifiedEditorFinishParams) => {
  return (
    !isActivationPromptDismissed &&
    !isAnyNodeLoading &&
    !_hasFlowErrors &&
    _.isEmpty(errors) &&
    _canUnpause
  );
};

export const isStepComplete = ({
  steps,
  nodeId,
}: {
  steps: Array<StepType>,
  nodeId: ID,
}) => {
  const step = steps.find(s => s.nodeId === nodeId);
  return step && step.isComplete;
};

export type EditorMode = 'create' | 'edit';

type GetEditorModeProps = {
  lastLiveAt: ?string,
  lastPausedAt: ?string,
};

export const getEditorMode = ({
  lastLiveAt,
  lastPausedAt,
}: GetEditorModeProps): EditorMode => {
  if (!lastPausedAt && !lastLiveAt) {
    return 'create';
  }
  return 'edit';
};

export const selectFlowErrorsForDrawer = (
  flow: Flow,
  services: ServicesMap,
  flowErrors: Array<any>,
  routerNodeId: ?ID
) => {
  const isEmptyNodeError = flowError => {
    const flowErrorNodeId = _.get(flowError, 'nodeId');
    const node = _.get(flow, ['nodes', flowErrorNodeId]);
    return node && NodeUtils.isEmptyNode(node, services);
  };

  const isFilterWithEmptyCriteriaError = flowError => {
    const flowErrorNodeId = _.get(flowError, 'nodeId');
    const node = _.get(flow, ['nodes', flowErrorNodeId]);
    return (
      NodeUtils.isFilterWithEmptyCriteria(node) &&
      flowError.pageKey === 'filter'
    );
  };

  const isPathWithEmptyCriteriaError = flowError => {
    const flowErrorNodeId = _.get(flowError, 'nodeId');
    const node = _.get(flow, ['nodes', flowErrorNodeId]);
    const isFilterCriteriaInvalid = filterCannotContinue(node);
    return isFilterCriteriaInvalid && flowError.pageKey === 'path';
  };

  const isStepAlreadyOpen = flowError => {
    const flowErrorNodeId = _.get(flowError, 'nodeId');
    return flowErrorNodeId && flowErrorNodeId === routerNodeId;
  };

  const flowErrorsForDrawer = _.chain(flowErrors)
    .reject(isEmptyNodeError)
    .reject(isFilterWithEmptyCriteriaError)
    .reject(isPathWithEmptyCriteriaError)
    .reject(isStepAlreadyOpen)
    .value();

  return flowErrorsForDrawer;
};

export const areAnySubStepsOpen = (
  pathname: string,
  subSteps: Array<SubStepType>
) => {
  // make sure 'app/editor' is sliced to '/editor' to ensure that
  // this function doesn't always return true as 'app' is one
  // of the possible page keys.
  const pathnameWithoutApp = pathname.replace('/app', '');
  return _.some(subSteps, subStep => pathnameWithoutApp.includes(subStep.key));
};

export const getFlowProgress = memoize((progressStepTotal: number) => [
  0,
  progressStepTotal,
]);

export const getZapSummary = (flow: Flow, services: ServicesMap) => {
  const zapInfo = {
    id: flow.id,
    title: flow.title,
    services: [],
    errorNodeDetails: [],
  };
  if (_.isEmpty(services)) {
    return zapInfo;
  }
  const errorNodeDetails = _.isEmpty(services)
    ? {}
    : _.mapValues(flow.nodes, node => {
        const service = getServiceAssociatedWithNode(node, services);
        return {
          app: service ? ServiceUtils.name(service) : undefined,
          slug: service ? _.get(service, ['slug']) : undefined,
          action: service ? NodeLabels.actionLabel(node, services) : undefined,
          stepNumber: getStepIndex(flow.id, flow.nodes, node.id) + 1,
        };
      });
  const flowServices = getFlowServices(flow);
  const flowServiceDetails = flowServices.map(
    flowService => services[flowService]
  );
  return {
    ...zapInfo,
    services: flowServiceDetails,
    errorNodeDetails,
  };
};
